import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, Send } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, SectionHeading } from "./Reveal";
import { WHATSAPP_LINK, PHONE_RAW, PHONE_DISPLAY } from "../data/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", email: "", interest: t.contact.interestOptions[0], message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(t.contact.required);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        interest: form.interest,
        message: form.message,
        language: lang,
      });
      toast.success(t.contact.success);
      setForm({ name: "", phone: "", email: "", interest: t.contact.interestOptions[0], message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0A0A0A] border border-[#2E2E2E] px-4 py-3.5 text-[#FFFFF0] text-sm font-light placeholder:text-[#FFFFF0]/30 focus:border-[#D4AF37] focus:outline-none transition-colors";

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-[#0A0A0A]" data-testid="contact-section">
      <div className="px-6 md:px-12 lg:px-20">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
          {/* Left: info */}
          <div>
            <SectionHeading overline={t.contact.overline} title={t.contact.title} text={t.contact.text} />

            <div className="mt-10 space-y-4">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-[#141414] border border-[#2E2E2E] p-5 hover:border-[#D4AF37]/50 transition-colors group"
                data-testid="contact-whatsapp"
              >
                <span className="h-11 w-11 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37]">
                  <MessageCircle size={20} strokeWidth={1.4} />
                </span>
                <div>
                  <p className="text-sm text-[#FFFFF0] group-hover:text-[#D4AF37] transition-colors">{t.contact.whatsapp}</p>
                  <p className="text-xs text-[#FFFFF0]/50 mt-0.5">{PHONE_DISPLAY}</p>
                </div>
              </a>
              <a
                href={`tel:${PHONE_RAW}`}
                className="flex items-center gap-4 bg-[#141414] border border-[#2E2E2E] p-5 hover:border-[#D4AF37]/50 transition-colors group"
                data-testid="contact-call"
              >
                <span className="h-11 w-11 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37]">
                  <Phone size={20} strokeWidth={1.4} />
                </span>
                <div>
                  <p className="text-sm text-[#FFFFF0] group-hover:text-[#D4AF37] transition-colors">{t.contact.call}</p>
                  <p className="text-xs text-[#FFFFF0]/50 mt-0.5">{PHONE_DISPLAY}</p>
                </div>
              </a>
            </div>
          </div>

          {/* Right: form */}
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="bg-[#141414] border border-[#2E2E2E] p-7 sm:p-9 space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs uppercase tracking-[0.14em] text-[#FFFFF0]/55 mb-2">{t.contact.name} *</label>
                  <input type="text" value={form.name} onChange={update("name")} className={inputCls} data-testid="form-name" required />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.14em] text-[#FFFFF0]/55 mb-2">{t.contact.phone} *</label>
                  <input type="tel" value={form.phone} onChange={update("phone")} className={inputCls} data-testid="form-phone" required />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.14em] text-[#FFFFF0]/55 mb-2">{t.contact.email}</label>
                <input type="email" value={form.email} onChange={update("email")} className={inputCls} data-testid="form-email" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.14em] text-[#FFFFF0]/55 mb-2">{t.contact.interest}</label>
                <select value={form.interest} onChange={update("interest")} className={inputCls} data-testid="form-interest">
                  {t.contact.interestOptions.map((opt, i) => (
                    <option key={i} value={opt} className="bg-[#0A0A0A]">{opt}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.14em] text-[#FFFFF0]/55 mb-2">{t.contact.message}</label>
                <textarea rows={4} value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} data-testid="form-message" />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-4 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.18em] hover:bg-[#B5952F] transition-colors disabled:opacity-60"
                data-testid="form-submit"
              >
                <Send size={15} strokeWidth={1.6} />
                {loading ? t.contact.sending : t.contact.submit}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
